import { useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { ShoppingBag, PartyPopper, X } from 'lucide-react';
import StatsWrapper from '../../utils/StatsWrapper';
import { reportDealFollowupAnswer } from '../../utils/dealFollowupApi';

// "מצאתי ב-בואתימצא! Botimza" as given - flagged in docs/deal-signal-design.md §8 item 6 as
// possibly garbled in transit ("בוטימזה" seems more likely); update this string once confirmed.
const FACEBOOK_GROUP_NAME = 'מצאתי ב-בואתימצא! Botimza';
const FACEBOOK_GROUP_URL = 'https://www.facebook.com/groups/3175949432574851';

const ORDER_ANSWERS = {
  ORDERED: 'ordered',
  DECLINED: 'declined',
  UNDECIDED: 'undecided',
};

const TOTAL_STEPS = 2; // the 3rd "share" screen is a bonus follow-on, not a counted step

const PRIMARY_BUTTON =
  'w-full py-2.5 rounded-xl font-semibold text-white bg-gradient-to-l from-orange-500 to-rose-500 ' +
  'shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm transition-all';
const SECONDARY_BUTTON =
  'py-2.5 rounded-xl font-medium text-gray-700 bg-white border border-gray-200 ' +
  'hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98] transition-all';
const GHOST_LINK =
  'w-full text-center text-sm text-gray-400 hover:text-gray-600 transition-colors pt-1';

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i < step ? 'w-4 bg-orange-500' : 'w-1.5 bg-gray-200'
          }`}
        />
      ))}
    </div>
  );
}

/**
 * Two-step content for the post-click-out follow-up popup toast
 * (docs/deal-signal-design.md §6). Step 1: did they order? Step 2: was it a
 * good deal? - shown regardless of the step 1 answer, since someone can
 * judge a good price without having ordered yet. A "good deal" answer
 * surfaces a share suggestion for the CoffeeBuy deals Facebook group.
 */
function DealFollowupToastContent({ toastId, asin, itemName, domain, href }) {
  const [step, setStep] = useState(1);
  const [orderAnswer, setOrderAnswer] = useState(null);

  const itemLabel = itemName || 'המוצר שקפצת אליו';

  const handleOrderAnswer = (answer) => {
    setOrderAnswer(answer);
    setStep(2);

    // Reported here, not just at the end of step 2 - if someone answers
    // "ordered" and then abandons the popup before the deal-quality
    // question, we still want the immediate purchase alert (docs/deal-signal-design.md §9/§10).
    reportDealFollowupAnswer({ asin, itemName, domain, href, orderAnswer: answer });
  };

  const handleGoodDealAnswer = (goodDeal) => {
    reportDealFollowupAnswer({ asin, itemName, domain, href, orderAnswer, goodDeal });
    if (goodDeal === 'yes') {
      setStep(3); // share suggestion
    } else {
      toast.dismiss(toastId);
    }
  };

  const handleShareClick = () => {
    StatsWrapper.track('deal.share_group_clicked', { asin });
    window.open(FACEBOOK_GROUP_URL, '_blank', 'noopener,noreferrer');
    toast.dismiss(toastId);
  };

  // react-hot-toast only offers corner/edge anchoring (no true center), and its per-toast
  // wrapper div always carries an inline `transform` (even a 0px translateY) - that makes
  // the wrapper a CSS containing block, so a plain `position: fixed` overlay nested inside
  // it would be "fixed" relative to that wrapper, not the viewport. Portaling straight to
  // document.body escapes it and gets a real, viewport-centered overlay.
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-auto">
      <div
        dir="rtl"
        className="w-[min(92vw,380px)] bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden text-gray-900"
      >
        <div className="h-1 bg-gradient-to-l from-orange-400 via-rose-400 to-orange-400" />

        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <span
                className={`flex items-center justify-center w-9 h-9 rounded-full shrink-0 ${
                  step === 3 ? 'bg-rose-100 text-rose-600' : 'bg-orange-100 text-orange-600'
                }`}
                aria-hidden="true"
              >
                {step === 3 ? <PartyPopper size={18} /> : <ShoppingBag size={18} />}
              </span>
              {step < 3 && <StepDots step={step} />}
            </div>
            <button
              onClick={() => toast.dismiss(toastId)}
              aria-label="סגור"
              className="text-gray-300 hover:text-gray-500 transition-colors -mt-1 -ml-1 p-1"
            >
              <X size={18} />
            </button>
          </div>

          {/* Item name stays as a persistent title across all three steps, so the
              question below never has to re-state what it's about. */}
          <p className="mb-1 text-[15px] font-bold leading-snug text-gray-900 line-clamp-2">
            {itemLabel}
          </p>

          {step === 1 && (
            <>
              <p className="mb-3 text-sm leading-snug text-gray-500">
                שמנו לב שחזרתם 👀 - הספקתם להציץ בו?
              </p>
              <p className="mb-4 text-[15px] font-semibold leading-snug text-gray-900">
                הזמנתם אותו בסוף?
              </p>
              <div className="flex flex-col gap-2">
                <button onClick={() => handleOrderAnswer(ORDER_ANSWERS.ORDERED)} className={PRIMARY_BUTTON}>
                  כן, הזמנתי! 🎉
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => handleOrderAnswer(ORDER_ANSWERS.DECLINED)} className={SECONDARY_BUTTON}>
                    לא, בסוף לא
                  </button>
                  <button onClick={() => handleOrderAnswer(ORDER_ANSWERS.UNDECIDED)} className={SECONDARY_BUTTON}>
                    עוד לא החלטתי
                  </button>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="mb-4 mt-2 text-[15px] font-semibold leading-snug text-gray-900">
                ולדעתך - זה היה דיל טוב?
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => handleGoodDealAnswer('yes')} className={PRIMARY_BUTTON}>
                  כן, דיל טוב 🔥
                </button>
                <button onClick={() => handleGoodDealAnswer('no')} className={SECONDARY_BUTTON}>
                  לא במיוחד
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="mb-4 mt-1 text-sm leading-snug text-gray-500">
                איזה כיף! ספרו על זה לקהילה ב’{FACEBOOK_GROUP_NAME}’ - שיזכו גם הם למצוא דילים כאלה.
              </p>
              <button onClick={handleShareClick} className={PRIMARY_BUTTON}>
                שתפו בקבוצה
              </button>
              <button onClick={() => toast.dismiss(toastId)} className={GHOST_LINK}>
                אולי בפעם אחרת
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DealFollowupToastContent;
